import { UpdateBanner } from '../components/UpdateBanner'
import { useEffect, useState } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@stores/authStore'
import { apiClient } from '@services/api'
import { SupplierListPage } from '@pages/procurement/SupplierListPage'
import { ProductListPage } from '@pages/procurement/ProductListPage'
import { PurchaseOrderListPage } from '@pages/procurement/PurchaseOrderListPage'
import { PurchaseOrderFormPage } from '@pages/procurement/PurchaseOrderFormPage'
import { PurchaseBillListPage } from '@pages/procurement/PurchaseBillListPage'
import { PurchaseBillEntryPage } from '@pages/procurement/PurchaseBillEntryPage'
import { PurchaseEntry } from '@pages/PurchaseEntry'
import { PurchaseBillsList } from '@pages/PurchaseBillsList'
import { PurchaseBillDetail } from '@pages/PurchaseBillDetail'
import { HomeDashboard } from '@pages/HomeDashboard'
import { RepackingEntry } from '@pages/RepackingEntry'
import { StockForSale } from '@pages/StockForSale'
import { BillingInvoice } from '@pages/BillingInvoice'
import { InvoicesList } from '@pages/InvoicesList'
import { InvoiceDetail } from '@pages/InvoiceDetail'
import { SalesCollections } from '@pages/SalesCollections'
import { PurchaseOrders } from '@pages/PurchaseOrders'
import { PurchaseOrderDetail } from '@pages/PurchaseOrderDetail'
import { AnalyticsDashboard } from '@pages/AnalyticsDashboard'
import { CompanySettings } from '@pages/CompanySettings'
import { RepackingListPage } from '@pages/repacking/RepackingListPage'
import { RepackingFormPage } from '@pages/repacking/RepackingFormPage'
import { RepackingDetailPage } from '@pages/repacking/RepackingDetailPage'
import { SalesOrderListPage } from '@pages/sales/SalesOrderListPage'
import { SalesOrderFormPage } from '@pages/sales/SalesOrderFormPage'
import { InvoiceListPage } from '@pages/sales/InvoiceListPage'
import { InvoiceDetailPage } from '@pages/sales/InvoiceDetailPage'
import { AnalyticsDashboardPage } from '@pages/analytics/DashboardPage'
import { CollectionsPage } from '@pages/analytics/CollectionsPage'
import { InventoryAnalyticsPage } from '@pages/analytics/InventoryAnalyticsPage'
import { Analytics } from '@pages/Analytics'
import { BatchExpirationPage } from '@pages/advanced/BatchExpirationPage'
import { FEFOPage } from '@pages/advanced/FEFOPage'
import { ReportsPage } from '@pages/advanced/ReportsPage'
import { StoreReliability } from '@pages/StoreReliability'
import { Ledger } from '@pages/Ledger'
import { Transactions } from '@pages/Transactions'
import { Reports } from '@pages/Reports'
import { ReportDetail } from '@pages/ReportDetail'

export function DashboardLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, token, refreshToken, setAuth } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileNav, setMobileNav] = useState(false)

  // Close the off-canvas menu whenever the route changes (mobile).
  useEffect(() => { setMobileNav(false) }, [location.pathname])

  // Refresh the signed-in user + company profile (name, GSTIN, logo) so cached sessions stay current.
  useEffect(() => {
    if (!token) return
    apiClient
      .getCurrentUser()
      .then((me) => { if (me?.id) setAuth(token, refreshToken || '', me) })
      .catch(() => {})
  }, [token])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const isActive = (id: string) => {
    const path = location.pathname
    switch (id) {
      case 'purchase-entry':
        return path.includes('/procurement/purchase-bills')
      case 'repacking-entry':
        return path.includes('/repacking')
      case 'stock-for-sale':
        return path.includes('/stock')
      case 'billing':
        return path.includes('/sales/invoices')
      case 'sales-collections':
        return path.includes('/collections') || path.includes('/sales/orders')
      case 'purchase-orders':
        return path.includes('/procurement/purchase-orders')
      case 'ledger':
        return path.includes('/ledger')
      case 'transactions':
        return path.includes('/transactions')
      case 'analytics':
        return path.includes('/analytics')
      case 'store-reliability':
        return path.includes('/store-reliability')
      case 'reports':
        return path.includes('/reports')
      default:
        return false
    }
  }

  const handleNavigation = (id: string) => {
    switch (id) {
      case 'purchase-entry':
        navigate('/dashboard/procurement/purchase-bills')
        break
      case 'repacking-entry':
        navigate('/dashboard/repacking')
        break
      case 'stock-for-sale':
        navigate('/dashboard/stock')
        break
      case 'billing':
        navigate('/dashboard/sales/invoices')
        break
      case 'sales-collections':
        navigate('/dashboard/collections')
        break
      case 'purchase-orders':
        navigate('/dashboard/procurement/purchase-orders')
        break
      case 'ledger':
        navigate('/dashboard/ledger')
        break
      case 'transactions':
        navigate('/dashboard/transactions')
        break
      case 'analytics':
        navigate('/dashboard/analytics')
        break
      case 'store-reliability':
        navigate('/dashboard/store-reliability')
        break
      case 'reports':
        navigate('/dashboard/reports')
        break
      default:
        navigate(`/dashboard/${id}`)
    }
  }

  // Which screens each role sees in the menu (owners and managers see everything).
  const ROLE_MENUS: Record<string, string[] | 'all'> = {
    OWNER: 'all',
    MANAGER: 'all',
    BILLER: ['billing', 'sales-collections', 'stock-for-sale', 'ledger', 'transactions', 'store-reliability', 'reports'],
    WAREHOUSE: ['purchase-entry', 'repacking-entry', 'stock-for-sale', 'purchase-orders'],
    SALESMAN: ['billing', 'sales-collections', 'stock-for-sale', 'store-reliability'],
  }
  const allowed = ROLE_MENUS[(user?.role || 'OWNER').toUpperCase()] || 'all'
  const canSee = (id: string) => allowed === 'all' || allowed.includes(id)

  const menuSections = [
    {
      title: 'Operations',
      items: [
        { n: 1, name: 'Purchase Entry', id: 'purchase-entry' },
        ...((user as any)?.tenant?.repackingEnabled === false ? [] : [{ n: 2, name: 'Repacking Entry', id: 'repacking-entry' }]),
        { n: 3, name: 'Stock for Sale', id: 'stock-for-sale' },
      ],
    },
    {
      title: 'Sales',
      items: [
        { n: 4, name: 'Billing / Invoice', id: 'billing' },
        { n: 5, name: 'Sales & Collections', id: 'sales-collections' },
      ],
    },
    {
      title: 'Procurement & Finance',
      items: [
        { n: 6, name: 'Purchase Orders', id: 'purchase-orders' },
        { n: 7, name: 'Ledger', id: 'ledger' },
        { n: 8, name: 'Transactions', id: 'transactions' },
      ],
    },
    {
      title: 'Insights',
      items: [
        { n: 9, name: 'Analytics Dashboard', id: 'analytics' },
        { n: 10, name: 'Store Reliability', id: 'store-reliability' },
        { n: 11, name: 'Reports', id: 'reports' },
      ],
    },
  ]

  return (
    <div className="flex h-screen bg-bg">
      <UpdateBanner />
      <div className={`sidebar-backdrop ${mobileNav ? 'show' : ''}`} onClick={() => setMobileNav(false)} aria-hidden="true" />
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? '' : 'collapsed'} ${mobileNav ? 'open' : ''}`}>
        <div className="brand">
          {user?.tenant?.logoUrl ? (
            <img src={user.tenant.logoUrl} alt={user.tenant.name} className="w-[38px] h-[38px] rounded-[7px] object-contain bg-white flex-shrink-0" />
          ) : (
            <div className="mark">{(user?.tenant?.name || 'X').charAt(0).toUpperCase()}</div>
          )}
          {sidebarOpen && (
            <div className="min-w-0">
              <div className="btitle truncate" title={user?.tenant?.name}>{user?.tenant?.name || 'Xora FMCG Ops'}</div>
              <div className="bsub">Xora FMCG Ops</div>
            </div>
          )}
        </div>

        <nav className="flex-1">
          {menuSections.map((section) => ({ ...section, items: section.items.filter((i) => canSee(i.id)) })).filter((s) => s.items.length > 0).map((section) => (
            <div key={section.title} className="navgroup">
              {sidebarOpen && <div className="glabel">{section.title}</div>}
              {section.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id)}
                  className={`nav-item ${isActive(item.id) ? 'active' : ''}`}
                  title={item.name}
                >
                  <span className="n">{item.n}</span>
                  {sidebarOpen && <span>{item.name}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-toggle mt-3 pt-3 px-[18px] border-t border-line">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full text-ink-soft hover:text-ink text-sm py-1"
            title={sidebarOpen ? 'Collapse' : 'Expand'}
          >
            {sidebarOpen ? '◄' : '►'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="app-header h-14 bg-panel border-b border-line flex items-center justify-between px-8 gap-3">
          <button type="button" className="menu-btn" onClick={() => setMobileNav((v) => !v)} aria-label="Open menu">☰</button>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink leading-tight">{user?.tenant?.name || 'FMCG Operations'}</div>
            {user?.tenant?.gstin && (
              <div className="text-[11px] text-ink-soft leading-tight truncate">
                GSTIN {user.tenant.gstin}
                {user.tenant.city ? ` · ${user.tenant.city}` : ''}
                {user.tenant.state ? `, ${user.tenant.state}` : ''}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-[12.5px] text-ink-soft header-user">
              {user?.fullName || (user as any)?.name || user?.email}
              {user?.role && <span className="ml-2 badge badge-ok">{user.role.charAt(0) + user.role.slice(1).toLowerCase()}</span>}
            </div>
            {user?.isPlatformAdmin && (
              <button onClick={() => navigate('/admin')} className="btn-ghost btn-sm" title="Xora platform console">
                Platform console
              </button>
            )}
            {user?.impersonatedBy && (
              <span className="badge badge-credit" title={`Support session opened by ${user.impersonatedBy}`}>Support access</span>
            )}
            <button onClick={() => navigate('/dashboard/settings')} className={`btn-ghost btn-sm ${location.pathname.includes('/settings') ? 'on' : ''}`} title="Company settings">
              ⚙
            </button>
            <button onClick={handleLogout} className="btn-ghost btn-sm">
              Logout
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="content">
            <Routes>
              <Route path="/" element={<HomeDashboard />} />
              <Route path="/settings" element={<CompanySettings />} />
              <Route
                path="/procurement/suppliers"
                element={<SupplierListPage />}
              />
              <Route
                path="/procurement/products"
                element={<ProductListPage />}
              />
              <Route
                path="/procurement/purchase-orders"
                element={<PurchaseOrders />}
              />
              <Route
                path="/procurement/purchase-orders/legacy-list"
                element={<PurchaseOrderListPage />}
              />
              <Route
                path="/procurement/purchase-orders/create"
                element={<PurchaseOrderFormPage />}
              />
              <Route
                path="/procurement/purchase-orders/:id"
                element={<PurchaseOrderDetail />}
              />
              <Route
                path="/procurement/purchase-bills"
                element={<PurchaseEntry />}
              />
              <Route
                path="/procurement/purchase-bills/list"
                element={<PurchaseBillsList />}
              />
              <Route
                path="/procurement/purchase-bills/legacy-list"
                element={<PurchaseBillListPage />}
              />
              <Route
                path="/procurement/purchase-bills/create"
                element={<PurchaseBillEntryPage />}
              />
              <Route
                path="/procurement/purchase-bills/:id"
                element={<PurchaseBillDetail />}
              />
              <Route
                path="/repacking"
                element={<RepackingEntry />}
              />
              <Route
                path="/repacking/legacy-list"
                element={<RepackingListPage />}
              />
              <Route
                path="/repacking/create"
                element={<RepackingFormPage />}
              />
              <Route
                path="/repacking/:id"
                element={<RepackingDetailPage />}
              />
              <Route
                path="/stock"
                element={<StockForSale />}
              />
              <Route
                path="/sales/orders"
                element={<SalesOrderListPage />}
              />
              <Route
                path="/sales/orders/create"
                element={<SalesOrderFormPage />}
              />
              <Route
                path="/sales/orders/:id"
                element={<SalesOrderFormPage />}
              />
              <Route
                path="/sales/invoices"
                element={<BillingInvoice />}
              />
              <Route
                path="/sales/invoices/list"
                element={<InvoicesList />}
              />
              <Route
                path="/sales/invoices/legacy-list"
                element={<InvoiceListPage />}
              />
              <Route
                path="/sales/invoices/create"
                element={<InvoiceDetailPage />}
              />
              <Route
                path="/sales/invoices/:id"
                element={<InvoiceDetail />}
              />
              <Route
                path="/analytics"
                element={<AnalyticsDashboard />}
              />
              <Route
                path="/analytics/legacy"
                element={<Analytics />}
              />
              <Route
                path="/collections"
                element={<SalesCollections />}
              />
              <Route
                path="/collections/legacy"
                element={<CollectionsPage />}
              />
              <Route
                path="/inventory"
                element={<InventoryAnalyticsPage />}
              />
              <Route
                path="/batch-expiration"
                element={<BatchExpirationPage />}
              />
              <Route
                path="/fefo"
                element={<FEFOPage />}
              />
              <Route
                path="/store-reliability"
                element={<StoreReliability />}
              />
              <Route
                path="/ledger"
                element={<Ledger />}
              />
              <Route
                path="/transactions"
                element={<Transactions />}
              />
              <Route
                path="/reports/:reportId"
                element={<ReportDetail />}
              />
              <Route
                path="/reports"
                element={<Reports />}
              />
              <Route
                path="/reports-legacy"
                element={<ReportsPage />}
              />
            </Routes>
          </div>
        </div>
      </main>
    </div>
  )
}
